<template>
  <div class="page theme-transition">
    <div class="receipt-container fade-in">
      <!-- Success Header -->
      <div class="receipt-header">
        <!-- Offline Badge -->
        <div v-if="isOfflineReceipt" class="offline-badge">
          <i class="fas fa-wifi-slash"></i>
          <span>OFFLINE - PENDING SYNC</span>
        </div>

        <div class="success-icon pulse">
          <i class="fas fa-check-circle"></i>
        </div>
        <h1 class="receipt-title">Payment Successful</h1>
        <p class="receipt-subtitle">{{ isOfflineReceipt ? 'Transaction saved offline - will sync when online' : 'Transaction completed successfully' }}</p>
      </div>

      <!-- Amounts Display -->
      <div class="amounts-section">
        <div class="amounts-grid">
          <!-- Total Paid -->
          <div class="amount-card">
            <div class="amount-icon">
              <i class="fas fa-credit-card"></i>
            </div>
            <h2 class="amount-value">{{ formatMoneyWithSymbol(receipt.paidAmount) }}</h2>
            <p class="amount-label">Total Paid</p>
          </div>

          <!-- Divider -->
          <div class="divider"></div>

          <!-- Cash Change -->
          <div class="amount-card">
            <div class="amount-icon">
              <i class="fas fa-hand-holding-usd"></i>
            </div>
            <h2 class="amount-value">{{ formatMoneyWithSymbol(cashChange) }}</h2>
            <p class="amount-label">Cash Change</p>
          </div>
        </div>

        <!-- Transaction Details -->
        <div class="transaction-details">
          <div class="detail-row">
            <span class="detail-label">Invoice Number:</span>
            <span class="detail-value" :class="{ 'offline-text': isOfflineReceipt }">
              {{ receipt.invoice || receipt.saleId || receipt.id || 'N/A' }}
              <span v-if="isOfflineReceipt" class="offline-indicator"> (OFFLINE)</span>
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Date & Time:</span>
            <span class="detail-value">{{ formatDateTime(receipt.timestamp || new Date()) }}</span>
          </div>
          <div v-if="receipt.reference" class="detail-row">
            <span class="detail-label">Reference / Note:</span>
            <span class="detail-value">{{ receipt.reference }}</span>
          </div>
          <div v-if="receipt.clientSaleId" class="detail-row">
            <span class="detail-label">Client ID:</span>
            <span class="detail-value">{{ receipt.clientSaleId }}</span>
          </div>
        </div>
      </div>

      <!-- QR Code Section -->
      <div v-if="qrCodeDataUrl" class="qr-code-section">
        <div class="qr-container">
          <img :src="qrCodeDataUrl" alt="ZRA Invoice QR Code" class="qr-code-img" />
        </div>
        <p class="qr-label">Scan for ZRA Invoice</p>
      </div>

      <!-- Email Receipt Section -->
      <div class="email-section">
        <label class="email-label" for="customerEmail">Customer Email(s)</label>
        <div class="email-input-row">
          <input id="customerEmail" type="text" v-model.trim="email" :disabled="!isOnline" placeholder="name@example.com, other@example.com" class="email-input"/>
          <button class="send-email-btn" type="button" @click="sendEmailReceipt" :disabled="!isOnline || !email">
            <i class="fas fa-paper-plane"></i>
            <span>Send Email Receipt</span>
          </button>
        </div>
        <div class="email-helper" v-if="!isOnline">
          <i class="fas fa-wifi-slash"></i>
          Feature unavailable while offline. Connect to the internet to email the receipt.
        </div>
        <div class="email-helper">
          <i class="fas fa-info-circle"></i>
          Separate multiple emails with commas
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="actions-section">
        <!-- Checkbox for Stay on POS After Sale -->
        <div class="checkbox-section" v-if="isOnline">
          <label class="checkbox-container">
            <input
                type="checkbox"
                v-model="stayOnPosAfterSale"
                @change="handleStayOnPosChange"
                class="checkbox-input"
            />
            <span class="checkbox-label" >
              <i class="fas fa-home"></i>
              Don't show this receipt again - Stay on POS after sale
            </span>
          </label>
        </div>

        <div class="actions-grid">
          <button @click="reprintLastInvoice" class="action-btn reprint-btn">
            <i class="fas fa-print"></i>
            <span>Reprint Receipt</span>
          </button>
          <button @click="nextSale" class="action-btn next-btn">
            <i class="fas fa-arrow-right"></i>
            <span>Next Sale</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Storage } from '@/services/Storage';
import axios from 'axios';
import QRCode from 'qrcode';
import LedConfig from '@/services/LedConfig';
// Centralised LED off-switch gate. When the user has disabled the LED
// display in Settings, we must not touch the COM port on Next-Sale
// either, so the serial port stays free for other programs.
import { isLedDisabled } from '@/services/ledGate';

export default {
  name: "ViewInvoicesView",
  data() {
    return {
      email: '',
      currency: '',
      stayOnPosAfterSale: false,
      disableEmailPrompt: false,
      isOnline: navigator.onLine,
      qrCodeDataUrl: '',
    }
  },
  computed: {
    receipt() {
      const invoice = this.$store.getters.getLatestInvoice;
      try {
        const androidData = JSON.parse(invoice.androidprintdata || '{}');
        const currencySymbol = androidData.Currency?.Symbol || 'K';
        return {
          ...invoice,
          currencySymbol
        };
      } catch (error) {
        console.error('Error parsing androidprintdata:', error);
        return {
          ...invoice,
          currencySymbol: 'K'
        };
      }
    },
    isOfflineReceipt() {
      return this.receipt.offline === true || this.receipt.provisional === true;
    },
    cashChange() {
      const change = parseFloat(this.receipt.change) || 0;
      const tip = parseFloat(this.receipt.tip) || 0;
      return change - tip;
    }
  },
  methods: {
    async sendEmailReceipt() {
      try {
        if (!this.isOnline) return; // Offline: do nothing
        const emailInput = (this.email || '').trim();
        if (!emailInput) return; // No email provided; nothing to do

        // Split by comma and filter valid emails
        const emails = emailInput.split(',').map(e => e.trim()).filter(e => e.length > 0);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        const validEmails = emails.filter(e => emailRegex.test(e));
        const invalidEmails = emails.filter(e => !emailRegex.test(e));

        if (validEmails.length === 0) {
          window.responses?.showWarning?.('Please enter a valid email address');
          return;
        }

        if (invalidEmails.length > 0) {
          window.responses?.showWarning?.(`Invalid email(s): ${invalidEmails.join(', ')}`);
        }

        const storeDB = await Storage.getCurrentStoreDB();
        const invoice = this.$store.getters.getLatestInvoice;
        if (!invoice) return;
        const invNumber = invoice?.invoice || invoice?.saleId || invoice?.id;
        if (!invNumber) return;

        // Send to each valid email
        const results = await Promise.allSettled(
          validEmails.map(email =>
            axios.post(`${window.franchiseTomcatApi}emailrecord/${storeDB}/emailSale`, {
              email,
              inv: invNumber,
            })
          )
        );

        const successCount = results.filter(r => r.status === 'fulfilled').length;
        const failCount = results.filter(r => r.status === 'rejected').length;

        if (successCount > 0) {
          const msg = validEmails.length === 1
            ? 'Email sent successfully'
            : `Sent ${successCount} of ${validEmails.length} emails`;
          window.Toast?.fire?.({ icon: 'success', title: msg });
          // Clear the email field after successful send
          this.email = '';
        }

        if (failCount > 0) {
          console.error(`Failed to send ${failCount} email(s)`);
        }
      } catch (err) {
        console.error('Failed to email receipt:', err);
        if (window?.errorHandler?.tomcatError) window.errorHandler.tomcatError(err, true);
        // Do not block workflow
      }
    },
    async handleStayOnPosChange() {
      try {
        // Show loading indicator
        this.$utils.showLoading();

        // Get current store
        const store = await Storage.getDefaultStore();

        // Call the API to save the setting
        const response = await axios.post(
            `${window.serverApi}admin/systemsettings/${store.StoreDB}/hide-next-sale`,
            { enabled: this.stayOnPosAfterSale }
        );

        if (response.data.status === 200 || response.status === 200) {
          // Update local storage
          const settings = await Storage.getDeviceSettings();
          settings.hideNextSale = this.stayOnPosAfterSale;
          await Storage.setDeviceSettings(settings);

          // Show success message
          window.Toast.fire({
            icon: 'success',
            title: this.stayOnPosAfterSale
                ? 'Will stay on POS after sale'
                : 'Will show receipt after sale'
          });
        }
      } catch (error) {
        console.error('Error saving hideNextSale setting:', error);
        // Revert checkbox on error
        this.stayOnPosAfterSale = !this.stayOnPosAfterSale;

        window.responses.showError('Failed to save setting. Please try again.');
      } finally {
        this.$utils.hideLoading();
      }
    },
    async handleDisableEmailPromptChange() {
      try {
        const settings = (await Storage.getDeviceSettings()) || {};
        settings.disableEmailPrompt = this.disableEmailPrompt === true;
        await Storage.setDeviceSettings(settings);
        window.Toast?.fire?.({
          icon: 'success',
          title: this.disableEmailPrompt ? "Won't ask to email receipt again" : 'Will ask to email receipt after sale'
        });
      } catch (e) {
        console.error('Failed to save disableEmailPrompt:', e);
        this.disableEmailPrompt = !this.disableEmailPrompt; // revert
        window.responses?.showWarning?.('Could not save preference. Please try again.');
      }
    },
    async nextSale() {
      // Attempt to email receipt if an email is present; do not block next actions
      try { this.sendEmailReceipt(); } catch (e) { /* non-blocking */ }
      try {
        const ledSettings = LedConfig.getEffective(await Storage.getDeviceSettings());

        // Off-switch: when the LED display is disabled, do not open the
        // COM port just to clear it.
        if (!isLedDisabled(ledSettings)) {
          const port = ledSettings?.ledPort || 'COM2';
          const baudRate = Number(ledSettings?.ledBaudRate || 2400);
          if (window.ledDisplay && window.ledDisplay.clear) {
            await window.ledDisplay.clear(port, baudRate);
          }
        } else {
          console.log('[LED] Next-sale clear skipped: LED display disabled in settings');
        }
      } catch (e) {
        console.error('[LED] Failed to clear display on next sale:', e);
      }
      // No full stock resync on Next. Local catalogue is correct post-sale
      // (Bug A + Bug B fixes make dirty/dirty-clear + decrement authoritative;
      // outbox.markSent -> releaseStockDirtyPending auto-clears the row).
      // Multi-device sync lag is bounded by syncService.js periodic sync
      // (15 min); 24h forced full sync (isFullSyncDue) heals drift; login
      // and store-switch still trigger fresh full syncs via syncDataLogin.
      return Promise.all([
        this.$store.dispatch(`getAllActiveSpecials`),
        this.proceed(),
      ]);
    },
    proceed() {
      return this.$store.dispatch('proceedToSales')
    },
    reprintLastInvoice() {
      return this.$store.dispatch(`reprintLastInvoice`);
    },
    formatMoneyWithSymbol(amount) {
      const currencySymbol = this.receipt.currencySymbol || 'K';
      const formattedAmount = parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
      return `${currencySymbol} ${formattedAmount}`;
    },
    formatDateTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    async fetchAndGenerateQRCode() {
      try {
        const invoice = this.$store.getters.getLatestInvoice;
        if (!invoice) return;

        // Try getting invoice number from various possible fields
        const invNumber = invoice.invoice || invoice.saleId || invoice.id;
        const storeDB = await Storage.getCurrentStoreDB();

        if (!invNumber || !storeDB) {
          console.warn('Missing invoice number or storeDB for QR code fetch');
          return;
        }

        if (!this.isOnline) {
          let qrData = `${serverApi}invoice/${storeDB}/scanInvoiceQRCode/offline/${invNumber}`;
          return this.qrCodeDataUrl = await QRCode.toDataURL(qrData);
        }

        const response = await axios.get(`https://lytningtomcat.chrilantech.com/Franchise_OnlineServer/invnumandi/${invNumber}/db/${storeDB}`);

        if (response.data && response.data.zraInvoiceQrCode) {
          this.qrCodeDataUrl = await QRCode.toDataURL(response.data.zraInvoiceQrCode);
        }
      } catch (error) {
        console.error('Error fetching/generating QR code:', error);
      }
    },
  },
  async mounted() {
    // Initialize theme via Vuex (respect saved preference)
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark') {
        this.$store.commit('setTheme', savedTheme);
      } else if (window.matchMedia) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        this.$store.commit('setTheme', mq.matches ? 'dark' : 'light');
      }
    } catch (_) {
      this.$store.commit('setTheme', 'light');
    }

    // Load initial preferences from device settings
    try {
      const settings = await Storage.getDeviceSettings();
      this.stayOnPosAfterSale = settings.hideNextSale === true;
      this.disableEmailPrompt = settings.disableEmailPrompt === true;
    } catch (error) {
      console.error('Error loading device settings:', error);
      this.stayOnPosAfterSale = false;
      this.disableEmailPrompt = false;
    }

    // Setup online/offline listeners
    this._onlineListener = () => { this.isOnline = true; };
    this._offlineListener = () => { this.isOnline = false; };
    window.addEventListener('online', this._onlineListener);
    window.addEventListener('offline', this._offlineListener);

    // Fetch QR Code
    this.fetchAndGenerateQRCode();
  },
  beforeDestroy() {
    // Clean up listeners
    if (this._onlineListener) window.removeEventListener('online', this._onlineListener);
    if (this._offlineListener) window.removeEventListener('offline', this._offlineListener);
  }
}
</script>

<style lang="scss" scoped>
.page {
  background: var(--bg-primary);
  min-height: 100vh;
  padding: 2rem 1rem;
  position: relative;
  font-family: var(--font-family);
}

.receipt-container {
  max-width: 700px; /* Increased width to accommodate longer amounts */
  margin: 0 auto;
  background: var(--card-bg);
  border-radius: 20px;
  box-shadow: var(--card-shadow);
  border: var(--card-border);
  overflow: hidden;
  position: relative;
}

.receipt-header {
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  padding: 3rem 2rem;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%);
    animation: shimmer 3s infinite;
  }

  .offline-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 165, 0, 0.95);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 700;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 10;
    animation: pulse-badge 2s infinite;

    i {
      font-size: 1rem;
    }

    @media (max-width: 768px) {
      top: 0.5rem;
      right: 0.5rem;
      font-size: 0.75rem;
      padding: 0.4rem 0.8rem;
    }
  }

  .success-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    position: relative;
    z-index: 1;
  }

  .receipt-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    position: relative;
    z-index: 1;
  }

  .receipt-subtitle {
    font-size: 1.1rem;
    opacity: 0.9;
    position: relative;
    z-index: 1;
  }
}

.amounts-section {
  padding: 3rem 2rem;
  background: var(--card-bg);
}

.amounts-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: center;
  margin-bottom: 2rem;
}

.amount-card {
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  transition: var(--transition-speed);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--accent-electric-blue), var(--accent-neon-purple));
    border-radius: 16px 16px 0 0;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--card-shadow-hover);
  }

  .amount-icon {
    font-size: 2rem;
    color: var(--accent-electric-blue);
    margin-bottom: 1rem;
  }

  .amount-value {
    font-size: 2.5rem;
    font-weight: 800;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
    line-height: 1.1;
    background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .amount-label {
    font-size: 0.9rem;
    color: var(--text-secondary);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.divider {
  width: 2px;
  height: 100px;
  background: linear-gradient(to bottom, transparent, var(--accent-electric-blue), transparent);
  border-radius: 2px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    background: var(--accent-electric-blue);
    border-radius: 50%;
    box-shadow: 0 0 20px rgba(var(--accent-electric-blue-rgb), 0.5);
  }
}

.transaction-details {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 1.5rem;
  border: var(--card-border);

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);

    &:last-child {
      border-bottom: none;
    }

    .detail-label {
      color: var(--text-secondary);
      font-weight: 500;
    }

    .detail-value {
      color: var(--text-primary);
      font-weight: 600;

      &.offline-text {
        color: #FF9500;
        font-weight: 700;
      }

      .offline-indicator {
        background: rgba(255, 165, 0, 0.15);
        color: #FF9500;
        padding: 0.2rem 0.5rem;
        border-radius: 4px;
        font-size: 0.75rem;
        font-weight: 700;
        margin-left: 0.5rem;
        display: inline-block;
        border: 1px solid rgba(255, 165, 0, 0.3);
      }
    }
  }
}

/* QR Code Styles */
.qr-code-section {
  padding: 2rem;
  background: var(--bg-primary);
  border-top: var(--card-border);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.qr-container {
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  display: inline-block;
}

.qr-code-img {
  width: 150px;
  height: 150px;
  display: block;
}

.qr-label {
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.9rem;
}

/* Email Receipt Section Styles */
.email-section {
  padding: 1.5rem 2rem;
  background: var(--bg-primary);
  border-top: var(--card-border);
}

.email-label {
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.email-input-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: center;
}

.email-input {
  width: 100%;
  height: 44px;
  border-radius: 10px;
  border: var(--card-border);
  background: var(--card-bg);
  color: var(--text-primary);
  padding: 0 12px;
  box-shadow: var(--card-shadow);
  transition: var(--transition-speed);
}
.email-input:focus {
  outline: none;
  border-color: var(--accent-electric-blue);
  box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb), 0.2);
}
.email-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.send-email-btn {
  height: 44px;
  padding: 0 16px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: #fff;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: var(--card-shadow);
  transition: var(--transition-speed);
}
.send-email-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--card-shadow-hover);
}
.send-email-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.email-helper {
  margin-top: 0.5rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .email-input-row {
    grid-template-columns: 1fr;
  }
}

.actions-section {
  padding: 2rem;
  background: var(--bg-primary);
  border-top: var(--card-border);
}

.checkbox-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--card-bg);
  border-radius: 12px;
  border: var(--card-border);
  box-shadow: var(--card-shadow);
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    .checkbox-label {
      color: var(--accent-electric-blue);
    }
  }
}

.checkbox-input {
  width: 20px;
  height: 20px;
  margin-right: 0.75rem;
  cursor: pointer;
  accent-color: var(--accent-electric-blue);
  flex-shrink: 0;

  &:checked + .checkbox-label {
    color: var(--accent-electric-blue);
    font-weight: 600;
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: var(--text-primary);
  transition: var(--transition-speed);
  font-weight: 500;

  i {
    font-size: 1.1rem;
    color: var(--accent-electric-blue);
  }
}

.actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.action-btn {
  padding: 1.2rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: var(--transition-speed);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: var(--card-shadow);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--card-shadow-hover);
  }

  i {
    font-size: 1.1rem;
  }
}

.reprint-btn {
  background: var(--secondary-button-bg);
  color: var(--secondary-button-text);
  border: var(--card-border);

  &:hover {
    background: var(--secondary-button-bg-dark);
  }
}

.next-btn {
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;

  &:hover {
    background: linear-gradient(135deg, var(--accent-electric-blue-dark), var(--accent-neon-purple-dark));
  }
}

.theme-toggle {
  position: fixed;
  top: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: var(--card-bg);
  color: var(--text-primary);
  box-shadow: var(--card-shadow);
  cursor: pointer;
  transition: var(--transition-speed);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;

  &:hover {
    transform: scale(1.1);
    box-shadow: var(--card-shadow-hover);
  }
}

// Animations
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse-badge {
  0% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(255, 165, 0, 0.4);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.fade-in {
  animation: fadeIn 0.6s ease;
}

.pulse {
  animation: pulse 2s infinite;
}

// Responsive Design
@media (max-width: 768px) {
  .page {
    padding: 1rem 0.5rem;
  }

  .amounts-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .divider {
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, transparent, var(--accent-electric-blue), transparent);

    &::before {
      width: 12px;
      height: 12px;
    }
  }

  .actions-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .amount-card {
    padding: 1.5rem;

    .amount-value {
      font-size: 2rem;
    }
  }

  .checkbox-section {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .checkbox-label {
    font-size: 0.85rem;

    i {
      font-size: 1rem;
    }
  }

  .checkbox-input {
    width: 18px;
    height: 18px;
  }
}
</style>
